import { 
  format, 
  parseISO, 
  addDays, 
  addYears, 
  differenceInMinutes,
  isValid,
  isDate
} from 'date-fns';

/**
 * Date utility functions to replace moment.js functionality
 * Provides consistent date formatting and manipulation across the application
 */

/**
 * Format a date string or Date object using the specified format
 * @param {string|Date} date - Date string or Date object
 * @param {string} formatString - Format string (e.g., 'YYYY-MM-DD', 'hh:mm')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatString) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different input types
  if (typeof date === 'string') {
    dateObj = parseISO(date);
  } else if (isDate(date)) {
    dateObj = date;
  } else {
    return '';
  }
  
  // Validate the date
  if (!isValid(dateObj)) {
    return '';
  }
  
  return format(dateObj, formatString);
};

/**
 * Create a date object from a string or Date
 * @param {string|Date} date - Date string or Date object
 * @returns {Date} Date object
 */
export const createDate = (date) => {
  if (!date) return new Date();
  
  if (typeof date === 'string') {
    return parseISO(date);
  }
  
  if (isDate(date)) {
    return date;
  }
  
  return new Date();
};

/**
 * Add days to a date
 * @param {string|Date} date - Base date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} New date with days added
 */
export const addDaysToDate = (date, days) => {
  const baseDate = createDate(date);
  return addDays(baseDate, days);
};

/**
 * Add years to a date
 * @param {string|Date} date - Base date
 * @param {number} years - Number of years to add (can be negative)
 * @returns {Date} New date with years added
 */
export const addYearsToDate = (date, years) => {
  const baseDate = createDate(date);
  return addYears(baseDate, years);
};

/**
 * Calculate difference in minutes between two dates
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {number} Difference in minutes
 */
export const getDifferenceInMinutes = (startDate, endDate) => {
  const start = createDate(startDate);
  const end = createDate(endDate);
  return differenceInMinutes(end, start);
};

/**
 * Get current date formatted with the specified format
 * @param {string} formatString - Format string
 * @returns {string} Formatted current date
 */
export const getCurrentDate = (formatString = 'yyyy-MM-dd') => {
  return format(new Date(), formatString);
};

/**
 * Check if a date is valid
 * @param {string|Date} date - Date to validate
 * @returns {boolean} True if date is valid
 */
export const isValidDate = (date) => {
  if (!date) return false;
  
  const dateObj = createDate(date);
  return isValid(dateObj);
};

// Export commonly used format strings
export const DATE_FORMATS = {
  ISO_DATE: 'yyyy-MM-dd',
  TIME: 'HH:mm',
  DATETIME: 'yyyy-MM-dd HH:mm',
  US_DATE: 'MM/dd/yyyy',
  EU_DATE: 'dd/MM/yyyy'
};
